import assertNever from "assert-never";
import { Instant, LocalDate } from "@js-joda/core";
import * as React from "react";
import { API } from "../API";
import {
  ScheduledVisitDocScreen,
  ScheduledVisitDocScreenProps,
} from "../components/ScheduledVisitDocScreen";
import { simpleAlert } from "../components/SimpleAlert";
import { UploadedFileViewerScreen } from "../components/UploadedFileViewerScreen";
import { isLoading } from "../Loading";
import {
  Icd10Codes,
  PatientId,
  PatientMedicationProfileCreateParams,
  PatientMedicationProfileWithDetails,
  VitalExtraDetails,
} from "../messages";
import {
  CaregiverPatientDocument,
  CaregiverPatientDocumentType,
  DocumentReplaceConstants,
  PatientDocumentScanCreate,
  PatientDocumentSubmissionDate,
} from "../messages/PatientDocuments";
import { STRINGS } from "../strings";
import { AppState } from "../system/AppState";
import { globalize, translate } from "../translate";

interface ScheduledVisitDocScreenActivityProps {
  api: API;
  appState: AppState;
  onBackClick: () => void;
  onClickAddPhysician: (patientId: PatientId) => void;
  requiredDocument: CaregiverPatientDocument;
  patientId: PatientId;
}

export class ScheduledVisitDocScreenActivity extends React.Component<ScheduledVisitDocScreenActivityProps> {
  constructor(props: ScheduledVisitDocScreenActivityProps) {
    super(props);
  }

  postAnswer = (
    doc:
      | CaregiverPatientDocument.Unanswered
      | CaregiverPatientDocument.Answered
      | CaregiverPatientDocument.Scanned
      | CaregiverPatientDocument.ManualSubmission,
    id: number,
    row: number,
    value: any,
    onSuccess?: () => void,
    createdAt?: Instant,
    vitalExtraDetails?: VitalExtraDetails
  ): Promise<void> => {
    return this.props.api
      .postPatientDocAnswer(this.props.requiredDocument.visitInstanceId, doc.documentTypeId, {
        answer: value,
        chartRow: row,
        questionId: id,
        createdAt: createdAt,
        vitalExtraDetails: vitalExtraDetails,
      })
      .then(() => {
        if (onSuccess !== undefined) {
          onSuccess();
        }
      });
  };

  postSubmitForm = (
    doc:
      | CaregiverPatientDocument.Unanswered
      | CaregiverPatientDocument.Answered
      | CaregiverPatientDocument.Scanned
      | CaregiverPatientDocument.ManualSubmission,
    submissionDate: LocalDate | null
  ): Promise<void> => {
    const patientDocumentSubmissionDate: PatientDocumentSubmissionDate = {
      submissionDate: submissionDate,
    };

    return this.props.api
      .postSubmitPatientDocument(
        this.props.requiredDocument.visitInstanceId,
        doc.documentTypeId,
        patientDocumentSubmissionDate
      )
      .then(() => {
        this.props.appState.incrementScheduledVisitSubmittedAmount(
          this.props.requiredDocument.visitInstanceId
        );
        this.props.onBackClick();
      });
  };

  getIcdCodeAutoCompleteOptions = (searchText: String): Promise<Icd10Codes> => {
    return this.props.api.getIcdCodeAutoCompleteOptions(searchText);
  };

  validateSubmissionDate() {
    if (!isLoading(this.props.appState.scheduledVisits)) {
      const visitDetails = this.props.appState.scheduledVisits.scheduledVisitDetails.find(
        (x) => x.id === this.props.requiredDocument.visitInstanceId
      );
      if (visitDetails !== undefined) {
        const scheduledVisitDate = globalize().formatInstant(
          visitDetails.publicVisitDetails.startTime,
          { date: "medium" }
        );
        const now = globalize().formatInstant(Instant.now(), { date: "medium" });
        return now === scheduledVisitDate;
      }
    }
    return false;
  }

  getVisitScheduledDate = (): LocalDate | null => {
    if (!isLoading(this.props.appState.scheduledVisits)) {
      const visitDetails = this.props.appState.scheduledVisits.scheduledVisitDetails.find(
        (v) => v.id === this.props.requiredDocument.visitInstanceId
      );
      if (visitDetails !== undefined) {
        return LocalDate.ofInstant(visitDetails.publicVisitDetails.startTime);
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  handleSearchMedications = (value: string) => {
    if (isLoading(this.props.appState.scheduledVisits)) {
      throw new Error("Can't search medications while scheduled visits are loading");
    }

    const visitDetails = this.props.appState.scheduledVisits.scheduledVisitDetails.find(
      (v) => v.id === this.props.requiredDocument.visitInstanceId
    );
    if (visitDetails === undefined) {
      throw new Error(
        `Can't search medications since visit ${this.props.requiredDocument.visitInstanceId} wasn't found in appstate`
      );
    }

    return this.props.api.searchMedicationsByPatientId(
      value,
      visitDetails.publicVisitDetails.patientId
    );
  };

  handleGetMedications = () => {
    if (isLoading(this.props.appState.scheduledVisits)) {
      throw new Error("Can't get medications while scheduled visits are loading");
    }

    const visitDetails = this.props.appState.scheduledVisits.scheduledVisitDetails.find(
      (v) => v.id === this.props.requiredDocument.visitInstanceId
    );

    if (visitDetails === undefined) {
      throw new Error(
        `Can't get medications since visit ${this.props.requiredDocument.visitInstanceId} wasn't found in appstate`
      );
    }

    return this.props.api.getPatientMedicationProfile(visitDetails.publicVisitDetails.patientId);
  };

  render() {
    type DocScreen = new () => ScheduledVisitDocScreen<ScheduledVisitDocScreenProps>;
    const DocScreen = ScheduledVisitDocScreen as DocScreen;

    const documentReplaceConstants: DocumentReplaceConstants = {
      agencyName: "agencyName",
      caregiverName: "caregiverName",
      patientName: "patientName",
      agencyAddress: "agencyAddress",
      agencyPhone: "agencyPhone",
    };

    if (!isLoading(this.props.appState.scheduledVisits)) {
      const visitDetails = this.props.appState.scheduledVisits.scheduledVisitDetails.find(
        (v) => v.id === this.props.requiredDocument.visitInstanceId
      );
      if (visitDetails !== undefined) {
        documentReplaceConstants.agencyName = visitDetails.publicVisitDetails.agency.name;

        if (visitDetails.publicVisitDetails.agency.address !== null) {
          documentReplaceConstants.agencyAddress = visitDetails.publicVisitDetails.agency.address;
        }

        if (visitDetails.publicVisitDetails.agency.officePhone !== null) {
          documentReplaceConstants.agencyPhone = visitDetails.publicVisitDetails.agency.officePhone;
        }

        documentReplaceConstants.patientName = visitDetails.confidentialVisitDetails.patientName;
      }
    }

    if (!isLoading(this.props.appState.appCaregiverState)) {
      documentReplaceConstants.caregiverName =
        this.props.appState.appCaregiverState.caregiver.firstName +
        " " +
        this.props.appState.appCaregiverState.caregiver.lastName;
    }

    const doc = this.props.requiredDocument;

    switch (doc.type) {
      case CaregiverPatientDocumentType.Answered:
      case CaregiverPatientDocumentType.ManualSubmission:
      case CaregiverPatientDocumentType.Unanswered:
        return (
          <DocScreen
            resubmissionReason={null}
            addNewPhysician={(...params) => this.props.api.addNewPhysician(...params)}
            onClickAddPhysician={this.props.onClickAddPhysician}
            getAllPhysicians={(...params) => this.props.api.getAllPhysicians(...params)}
            getPatientPrimaryPhysician={(...params) =>
              this.props.api.getPatientPrimaryPhysician(...params)
            }
            setPatientPhysician={(...params) => this.props.api.setPatientPhysician(...params)}
            patientId={this.props.patientId}
            onBackClick={this.props.onBackClick}
            title={doc.title}
            docFormat={doc.content}
            initialAnswers={"answers" in doc ? doc.answers : undefined}
            isScannable={doc.isScannable}
            onAnswerQuestion={(id, row, value, onSuccess, createdAt, vitalExtraDetails) =>
              this.postAnswer(doc, id, row, value, onSuccess, createdAt, vitalExtraDetails)
            }
            onSubmitForm={(submissionDate: LocalDate | null) =>
              this.postSubmitForm(doc, submissionDate)
            }
            onValidateSubmissionDate={() => this.validateSubmissionDate()}
            readonly={"submittedAt" in doc && doc.submittedAt !== null}
            getLastAnswers={() =>
              this.props.api.getLastAnswers(
                this.props.requiredDocument.visitInstanceId,
                doc.versionId
              )
            }
            getIcdCodeAutoCompleteOptions={(searchText: string) =>
              this.getIcdCodeAutoCompleteOptions(searchText)
            }
            documentReplaceConstants={documentReplaceConstants}
            onScanUpload={(a: string, showLoader: () => void, hideLoader: () => void) => {
              const data: PatientDocumentScanCreate = {
                base64: a,
                documentId: doc.documentTypeId,
                documentVersion: doc.versionId,
                documentTitle: null,
              };

              showLoader();

              this.props.api
                .uploadScannedScheduledVisitDocument(
                  data,
                  this.props.requiredDocument.visitInstanceId
                )
                .then(() => {
                  this.props.onBackClick();
                })
                .catch(() => {
                  simpleAlert({
                    icon: "error",
                    message: translate(STRINGS.UPLOAD_ERROR),
                    title: translate(STRINGS.ERROR),
                  });
                  hideLoader();
                });
            }}
            getScheduledDate={() => this.getVisitScheduledDate()}
            searchMedications={this.handleSearchMedications}
            getPatientMedicationProfile={this.handleGetMedications}
            onCreateMedication={(params: PatientMedicationProfileCreateParams) =>
              this.props.api.postPatientMedicationProfileItem(
                this.props.requiredDocument.visitInstanceId,
                params
              )
            }
            onUpdatedMedication={(medication: PatientMedicationProfileWithDetails) =>
              this.props.api.editPatientMedicationProfileItem(
                this.props.requiredDocument.visitInstanceId,
                {
                  id: medication.id,
                  dose: medication.dose,
                  frequency: medication.frequency,
                  from: medication.from,
                  medicationId: medication.medicationId,
                  status: "ACTIVE",
                  until: medication.until,
                  notes: medication.notes,
                }
              )
            }
            onDeleteMedication={(medication: PatientMedicationProfileWithDetails) =>
              this.props.api.editPatientMedicationProfileItem(
                this.props.requiredDocument.visitInstanceId,
                {
                  id: medication.id,
                  dose: medication.dose,
                  frequency: medication.frequency,
                  from: medication.from,
                  medicationId: medication.medicationId,
                  status: "DISCONTINUED",
                  until: medication.until,
                  notes: medication.notes,
                }
              )
            }
            getPatientContractDiagnosisCodes={() =>
              this.props.api.getPatientDiagnosisCodesByVisitInstanceId(
                this.props.requiredDocument.visitInstanceId
              )
            }
            onCreateDiagnosis={(params) =>
              this.props.api.createPatientDiagnosisCodeByVisitInstanceId(
                this.props.requiredDocument.visitInstanceId,
                params
              )
            }
            onEditDiagnosis={(id, params) => this.props.api.updatePatientDiagnosisCode(id, params)}
            onDeleteDiagnosis={(id) => this.props.api.deletePatientDiagnosisCode(id)}
            onCheckForOtherNursingQuestionsOnDocuments={() => {
              switch (this.props.requiredDocument.type) {
                case CaregiverPatientDocumentType.Unanswered:
                case CaregiverPatientDocumentType.Answered:
                case CaregiverPatientDocumentType.ManualSubmission:
                case CaregiverPatientDocumentType.Scanned:
                  return this.props.api.checkForOtherNursingQuestionsOnDocuments({
                    visitInstanceId: this.props.requiredDocument.visitInstanceId,
                    documentTypeId: this.props.requiredDocument.documentTypeId,
                  });
                case CaregiverPatientDocumentType.FreeScan:
                  console.warn("Free scan document type not supported for this call");
                  return Promise.resolve({
                    documentsToBeRegenerated: [],
                  });
                default:
                  assertNever(this.props.requiredDocument);
              }
            }}
          />
        );
      case CaregiverPatientDocumentType.FreeScan:
      case CaregiverPatientDocumentType.Scanned:
        return (
          <UploadedFileViewerScreen
            onBackClick={() => this.props.onBackClick()}
            title={doc.title}
            url={doc.pdfUrl}
          />
        );
      default:
        assertNever(doc);
    }
  }
}
